<template>
  <b-form v-if="ready" class="public-invoice" @keydown="form.errors.clear($event.target.id)">
    <link
      href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap"
      rel="stylesheet"
    />

    <template v-if="!invalid && !completed && invoice">
      <header class="bg-white mb-3 shadow-sm fixed-top">
        <div class="container">
          <div class="row">
            <div class="col-9 col-sm-6 d-flex flex-wrap align-items-center">
              <div class="d-none d-lg-inline-block">Invoice Total</div>
              <h2 class="pr-3 px-lg-3 header-total">
                {{ formatCurrency(invoice.total_with_card_fee) }}
              </h2>
              <div v-if="isOverdue" class="header-overdue px-2 py-1">OVERDUE</div>
              <div class="text-muted header-date ml-md-2">
                Due: {{ dueDate.format("Do MMM YYYY") }}
              </div>
            </div>
            <div class="col-3 col-sm-6 text-right py-4">
              <a
                v-if="!isAndroid"
                class="download-pdf cursor"
                target="_blank"
                @click.prevent="openPdf"
              >
                <span v-if="generatingPdf">
                  <span class="d-none d-lg-inline-block pr-2">Downloading</span>
                  <font-awesome-icon spin :icon="['fal', 'spinner']" />
                </span>
                <span v-else>
                  <span class="d-none d-lg-inline-block pr-2">Download PDF</span>
                  <font-awesome-icon :icon="['fal', 'cloud-download']" />
                </span>
              </a>

              <b-button
                v-if="invoice.status === 'Paid'"
                variant="secondary"
                size="lg"
                class="mr-3 d-none d-lg-inline-block cursor-not-allowed"
              >
                Paid
              </b-button>
              <b-button
                v-else-if="!disabled"
                v-b-modal.payModal
                variant="danger"
                size="lg"
                class="mr-3 d-none d-lg-inline-block"
              >
                Pay Now
              </b-button>
            </div>
          </div>
        </div>
      </header>

      <footer class="fixed-bottom bg-white mt-3 px-3 py-4 shadow-lg d-lg-none">
        <div class="container">
          <b-button
            v-if="invoice.status === 'Paid'"
            variant="secondary"
            size="lg"
            class="d-block w-100 cursor-not-allowed"
          >
            Paid
          </b-button>
          <b-button
            v-else-if="!disabled"
            v-b-modal.payModal
            variant="danger"
            size="lg"
            class="d-block w-100"
          >
            Pay Now
          </b-button>
        </div>
      </footer>
    </template>

    <template v-if="completed">
      <div class="d-flex align-items-center justify-content-center position-absolute w-100 h-100">
        <div class="w-75 text-center shadow-sm bg-white rounded">
          <div class="flex w-100">
            <div class="flex text-center brand-header-content">
              <img
                :src="invoice.job.brand.logo"
                :alt="invoice.job.brand.name"
                class="brand-header-logo rounded-circle bg-white"
              />
              <h4 class="brand-header-name mb-1">{{ invoice.job.brand.name }}</h4>
              <div class="text-muted">
                Invoice: #{{ invoice.invoice_number | trimLeadingZeros }}
              </div>
              <div class="my-3">
                Thank you! Your payment has been processed successfully.<br />
                Please check your emails for a receipt.
              </div>
              <b-button
                variant="primary"
                class="d-none d-lg-inline-block mb-4"
                @click="$root.reload()"
              >
                View Invoice Again
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-if="invalid && !completed">
      <div class="d-flex align-items-center justify-content-center position-absolute w-100 h-100">
        <div class="w-75 text-center shadow-sm bg-white rounded">
          <div class="flex w-100">
            <div class="flex text-center">
              <div class="mt-4 mb-4">The invoice is invalid.</div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <b-modal v-if="invoice && invoice.total" id="payModal" hide-footer centered>
      <template #modal-header="{ close }">
        <div class="flex w-100">
          <div class="flex text-center brand-header-content">
            <img
              :src="invoice.job.brand.logo"
              :alt="invoice.job.brand.name"
              class="brand-header-logo rounded-circle bg-white"
            />
            <h4 class="brand-header-name mb-1">{{ invoice.job.brand.name }}</h4>
            <div>Invoice: #{{ invoice.invoice_number | trimLeadingZeros }}</div>
          </div>
        </div>
        <button class="modal-close" @click="close()">
          <font-awesome-icon :icon="['fal', 'times']" />
        </button>
      </template>

      <div class="payment-method-select">
        <div class="text-black text-16 mb-3">Your preferred payment method:</div>
        <div class="row payment-method-select-group">
          <div
            class="col cursor"
            :class="selectedPaymentMethod === 'card' ? 'selected' : ''"
            @click="selectedPaymentMethod = 'card'"
          >
            Credit Card
          </div>
          <div
            class="col cursor"
            :class="selectedPaymentMethod !== 'card' ? 'selected' : ''"
            @click="selectedPaymentMethod = 'bank'"
          >
            Bank Transfer
          </div>
        </div>
      </div>

      <template v-if="selectedPaymentMethod === 'card'">
        <div class="row justify-content-center mt-3 mb-1">
          <div class="col-12">
            <div class="form-component-row-single form-row">
              <label for="number" class="d-none">Card Number</label>
              <div class="form-component-field col-12 mb-3">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">
                      <img
                        v-if="invoice.job.brand.disable_amex"
                        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGwAAAAkCAYAAABsbd/MAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAAbKADAAQAAAABAAAAJAAAAACRWMmWAAAIc0lEQVRoBe1bC3BU1Rn+7t3dZDebF8kmEAIkQLKsQFoDQXlJY422oiJllFbHR1sEqxZlptapM06dvqYzdaxV6zDqONo6ik3VVmmlDIiIRmgbCUyEpElKSHgsCSHvbPZ19/b8Z/debpbN7t0kZLJ2/5nd8/7Pf/7v/v/5z7m7wtItL5qGDjzz80AgcC+AAvZJJHILgvBPSZIeamlpORYueMYdO22+zoHnZRk3sras8PYpXu4TBOwy5WdsHdhxS5ciq+BwOH7NwPqJUpGgqZPJ/ZWmpiZ1YbSO1K+/uZ8lX2OfRKYDnn13qmsQQ5aVyAsi2QsMBkOVdhHmte8UsbK6UG1bguXXhNbCxRbZd6K5wYj6lmW5UNsgeKSZ2nIi57VrIcCSlEAaSAKWQGCRqEnAkoAlmAYSTNykhSUYYMaJkNfhDuDKYQkFPpmzc5oEHLEY0Gge+TzYrDLKZ0lYVBDg/Ya8AupOi6htN0yEGP8XPMYF2BKXhIc7vSj1BgEI11hziojn8lPQnidiW6UXt5T5w7vwcq9LwPaaFLz1+bjEicj7y1Y5Zg1t6vLi+92+qPogIH/nGYBwvQeZjvRR+2anyXic9amy+/HIO6kgy0tSZA2MCbC7L8QGi6Yz5fpgq2K3RR0B+NMA/5zRQaP+y4okPLXegwerzVS87LS6LA8L5mSq8/j8Mj4+0oG2jiG1rmi6FVUVM3i5/kQvOrrdavnjo51oOT3A2yypBqy/ZjbmF2bAajagvcOF92tO48x5l8qrZFYGVi7Kg8ko4HBzD+qautU2vZm4AbOz/WrLheiWpUyevqIHSAm6S2NbPwI5qQikm5TmiOmqeRI2r/Th5c+i94s4OM7KT+vPM+WJ2HRzCW6rnMNHHz/ZiyWbdqmctm104IH1dl6u2rYX6xgoTz1Qzss3PrqPA/bN5TPxymPLkZuVqo5TMtv/2sSztuxUfPrCDchOT+Hlt/e3465f1CjddKdxA/Y95gZHhhKR5zLPd8HALEylgAxj2wC8i3LUqtEyd1X48NpBI3zy5XeNH9V1gD4mg4BlV+RiYXE2SLldvR6Q1dxRVczFJCAJ4Duvn6uKffxkHwrz0rDjyWtgYft176AXe2vPoX/IhwpHDhrb+tS+991UooJFlUsXxNaDOliTiQuwNEnGiiFJM3z0bGrx8CWNInMngj8AmT3V0Yj2tEq7hD3/iUu8aCxjttUxF3Xtkhk42+XClSU5TPFO3LSiUFXyC+8GLWVhcfAtDYHT0ePGPd+Yx8GiCX7P+vzyD/WXzGUQBWxZV8LrT3UOYXa+FXML0tUH45IBUSqiay5soMMTgIm9XNJDxhyNdSkDmJUJbn2AF+fqm0dhPd5097+dyLSawN6voSL09H937XzOlizmzb0ned5RFNzzGpmLJ+rsc/OUvn70nYV44t4ybplqJct8a81szLSxTZzR4y8e4Sl9VThy1bzeTFyAZerTNZ9bSIvcWXBHDu3DBS7MinxUCO83UWUKALr7PSjItfDAgVzddcziiN7Y04phj4Tp08yqxZE7JPrHobN4fXcrz5NbfOKexTj2x5tRbr/o8n64YQFvP3SsC385cArDoWPQ1cwFx0txAdYfz/nWG5m1bNLHpHs48vh4FxhP/32HO3j3yvJ83H3DXGZtwdEv72zhGcUdUqGhPWhhlN/8m0O4/aefoNU5SEVuTdU/Ww1yhQTc8kU2Xv/67hOQmJc52hyMDpddEaznjTq/4tJKO3uC9JI0EBkY2apvX2rrvvwBR/haPjp8jlfRHvOD9aU8T2G+Yk2Ooou/MmgIWZjCYycL4Zds+kAN84mHjUWND4YiTOr30AY7PmGRYikL74kU18sLOr/0aS/ErIudH+j2YrSbDe2c3jYLLNookTXKmakxAw7i4ZME1Pw3MuDaOSY6TxGeQjNyLDyrWBcVyuZlK81oYBHgVcylURT594NnebBCFjgtIxi2Oy8MQ2L7/W3XFqljKALVEoX4dDZTznLattHycQFGTF7LTcGvnBc32tEYDzdbYVnMXEToHEb9/LOto3UfUf+3LwzoGpp8C6MDMymPlEhEESPtOQoph2wKQuhAvG7VLH5GU85pSj+Kyx5+thYUytO+RvTj7XVoPh10o/lZZrz02NW8fhkLPC4rYPszDKhhm9mqGOF9wC1i8F9ZSF/NDs+MAjYLJPaJRR1sTW/UmlCaF0Dzef0uOBZfve1v7WvDhjWzePdXd7XyPUc7llzhsZA7bD03yG9GyMVNyzTjDAvZj7T04Ok/NfBbjPtvLQX172MAP/92o5YNNrMwP91sRKEOnWgHCna7Pe74mc5jvz3jRhm79YhF1rIBmCs98JblxHSHdAl83w4zTnYJyGJRcDxWJorio42NjU8r8liuq14RkP2fKeVETkXBuHL4w40HaQ1jeoRd7FZga5EZ1dkmRIOM2qqd2Xjy1HScc0WfquaEAbe/auFWRTcc8YCVyGDEK3vce5gyASn1WfbqpDrbiLX9fnxVeR/GYmEn43qUvQ/7INMIJ/nw48C7DezXnIslXDVHwsLQ+zAXu5WvbRfxYZMB9WcnP8hQ1pJI6ZgBUxZJgLxiC0ZGSl2klAB+v97IP5Hak3X6NBDdT+njkew1iRpIAjaJyp6IqZKATYQWJ5FHErBJVPZETJUEbCK0OIk8CLDY90yTKNA4pgr+uCLEgF2UB6/Ox8FwqgzVrkVkL+z2TBXBxiGH5Pf7R/xAwjGvh+6COsfBc6oM7QythcvDbnTEreyvOl9MFenGIAd5iEfC/4H5+Uv3+wRR+DZrS2TQOmkNtBZFL/xKnP426655rtzn8yXUf6qYd3Czh602/J+XyuIota17L2PQ5VoqyuLIdxvaTlMwHxACvRnTDLXn/7xxhGv/HwLxvVa8dkb+AAAAAElFTkSuQmCC"
                        style="width: 54px"
                        alt=""
                      />
                      <img
                        v-else
                        src="data:image/png;base64,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"
                        style="width: 81px"
                        alt=""
                      />
                    </span>
                  </div>

                  <input
                    id="number"
                    v-model="form.number"
                    type="text"
                    name="number"
                    placeholder="Card Number"
                    required="required"
                    autocomplete="chrome-off"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="form-component-row-single form-row">
              <label for="name" class="d-none">Name</label>
              <div class="form-component-field col-12 mb-3">
                <div class="input-group">
                  <input
                    id="name"
                    v-model="form.name"
                    type="text"
                    name="number"
                    placeholder="Name on Card"
                    required="required"
                    autocomplete="chrome-off"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="form-component-row-single form-row">
              <label for="card_time" class="d-none">Expiry</label>
              <div class="form-component-field col-12 mb-3">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">
                      <font-awesome-icon :icon="['fal', 'calendar-day']" />
                    </span>
                  </div>

                  <input
                    id="card_time"
                    v-model="cardTime"
                    type="text"
                    maxlength="5"
                    name="card_time"
                    placeholder="MM/YY"
                    required="required"
                    autocomplete="chrome-off"
                    class="form-control"
                    @keydown="adjustCCTime"
                    @input="parseTime"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="form-component-row-single form-row">
              <label for="cvc" class="d-none">Cvc</label>
              <div class="form-component-field col-12 mb-3">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">
                      <font-awesome-icon :icon="['fal', 'lock']" />
                    </span>
                  </div>

                  <input
                    id="cvc"
                    v-model="form.cvc"
                    type="text"
                    :minlength="invoice.job.brand.disable_amex ? 3 : 4"
                    :maxlength="invoice.job.brand.disable_amex ? 3 : 4"
                    name="cvc"
                    placeholder="CVC"
                    required="required"
                    autocomplete="chrome-off"
                    class="form-control"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-if="tribute" class="tribute-selection col-12 my-3">
            <div class="row mb-3 text-black">
              <div class="col">
                Add a Donation in support of
                <a :href="tribute.video" target="_blank" class="tribute-link">
                  {{ tribute.name }}
                </a>
              </div>
            </div>
            <div class="row">
              <div class="col-3 tribute-logo-wrapper">
                <img :src="tribute.logo" :alt="tribute.name" class="tribute-logo" />
              </div>
              <div class="col-9">
                <div class="row tribute-grid-group rounded overflow-hidden text-16">
                  <div
                    class="col-12 tribute-grid border-bottom"
                    :class="selectedTribute === 'roundup' ? 'selected' : ''"
                    @click="selectTribute('roundup')"
                  >
                    <span class="text-16">
                      Round up to
                      {{ formatCurrency(roundupTarget, "$", 0) }}
                    </span>
                  </div>
                  <div
                    class="col-3 tribute-grid"
                    :class="selectedTribute === 5 ? 'selected' : ''"
                    @click="selectTribute(5)"
                  >
                    <span class="text-14">$</span>5
                  </div>
                  <div
                    class="col-3 tribute-grid"
                    :class="selectedTribute === 10 ? 'selected' : ''"
                    @click="selectTribute(10)"
                  >
                    <span class="text-14">$</span>10
                  </div>
                  <div
                    class="col-3 tribute-grid"
                    :class="selectedTribute === 20 ? 'selected' : ''"
                    @click="selectTribute(20)"
                  >
                    <span class="text-14">$</span>20
                  </div>
                  <div
                    class="col-3 tribute-grid"
                    :class="selectedTribute === 'other' ? 'selected' : ''"
                    @click="selectTribute('other')"
                  >
                    <span class="text-16">Other</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="selectedTribute === 'other'" class="row mt-2">
              <div class="col-12">
                <div class="form-component-row-single form-row">
                  <label for="tribute_amount" class="col-4" style="line-height: 40px">
                    Donate Amount
                  </label>
                  <div class="form-component-field col-8 mb-3">
                    <div class="input-group">
                      <input
                        id="tribute_amount"
                        v-model="form.tribute_amount"
                        type="text"
                        placeholder="Donate amount"
                        class="form-control"
                        style="height: 40px"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="message" class="row justify-content-center mb-4">
          <div class="col-12">
            <div class="alert alert-danger mb-0" role="alert">
              {{ message }}
            </div>
          </div>
        </div>

        <div class="text-center">
          <div>
            <div class="mb-1">For invoice amount</div>
            <h1 class="mb-0">{{ formatCurrency(dueAmount) }}</h1>
            <div class="mb-3">
              Including card fee
              {{ formatCurrency(dueAmount - invoice.lines_total - form.tribute_amount) }}
              ({{ invoice.job.brand.card_fee }}%)
            </div>
          </div>

          <div class="border border-default rounded">
            <vue-signature-pad
              id="signature"
              ref="signaturePad"
              width="100%"
              height="160px"
              :options="{
                onBegin: () => {
                  $refs.signaturePad.resizeCanvas();
                },
                onEnd,
              }"
            />
          </div>

          <button
            class="btn btn-lg mt-3 mb-4 w-100"
            :class="signed ? 'btn-danger' : 'btn-primary cursor-not-allowed'"
            :disabled="form.notReady() || !signed"
            @click.prevent="onSubmit"
          >
            <font-awesome-icon v-if="form.busy" spin :icon="['fal', 'spinner']" />
            <span v-else-if="signed">Confirm and Pay</span>
            <span v-else>Please Sign Above</span>
          </button>
        </div>
      </template>
      <template v-else>
        <div class="py-2"><b>Amount Payable</b></div>
        <div class="pb-3">
          {{ formatCurrency(invoice.lines_total) }}
        </div>

        <div class="pt-2"><b>Bank Transfer Details</b></div>
        <div class="whitespace-preline pt-2">{{ invoice.job.brand.bank_account }}</div>
        <div class="mt-2">
          Reference:
          {{ invoice.is_quote ? "QUOTE" : "INVOICE" }}
          #{{ invoice.invoice_number }}
        </div>

        <div class="pt-4"><b>PayID</b></div>
        <div class="whitespace-preline pt-2"><u>PayID@TheMRGroup.com.au</u><br /></div>
        <div class="mt-2">
          Reference:
          {{ invoice.is_quote ? "QUOTE" : "INVOICE" }}
          #{{ invoice.invoice_number }}
        </div>
      </template>
    </b-modal>

    <template v-if="!invalid && !completed && invoice">
      <div class="invoice-container container overflow-hidden py-3">
        <iframe :src="embedUrl"></iframe>
      </div>
    </template>
  </b-form>
</template>

<script type="text/babel">
import Vue from "vue";
import moment from "moment";
import VueSignaturePad from "vue-signature-pad";
import pageview from "../../imports/google";
import format from "../../mixins/format";
import iframe from "../../mixins/iframe";

Vue.use(VueSignaturePad);

export default {
  mixins: [format, iframe],

  data() {
    return {
      embedUrl: null,
      fromMobile: false,
      invoice: null,
      message: null,
      completed: false,
      invalid: false,

      form: new window.AppForm({
        number: "",
        name: "",
        exp_month: "",
        exp_year: "",
        cvc: "",
        appointment_id: "",
        signature: null,
        tribute: null,
        tribute_amount: 0,
      }),

      cardTime: "",
      signed: false,
      tribute: null,
      selectedTribute: null,
      selectedPaymentMethod: "card",
    };
  },

  computed: {
    hasPoReference() {
      return this.invoice.job && this.invoice.job.po_reference;
    },
    disabled() {
      return this.hasPoReference || this.invoice.disable_credit_card;
    },
    dueDate() {
      let paymentTerms = 0;

      switch (this.invoice.job.customer.payment_terms) {
        case "7 Days":
          paymentTerms = 7;
          break;

        case "30 Days":
          paymentTerms = 30;
          break;

        case "45 Days":
          paymentTerms = 45;
          break;

        case "30 Days from End of Month":
          paymentTerms =
            moment(this.invoice.created_at)
              .endOf("month")
              .diff(moment(this.invoice.created_at), "days") + 30;
          break;

        default:
          paymentTerms = 0;
      }

      return moment.utc(this.invoice.created_at).local().startOf("day").add(paymentTerms, "days");
    },

    isOverdue() {
      return this.dueDate.isBefore(moment().utc().startOf("day"));
    },

    isAndroid() {
      return /(android)/i.test(navigator.userAgent);
    },

    cardFeeFactor() {
      return 1 + parseFloat(this.$lodash.get(this.invoice, "job.brand.card_fee", 0)) / 100;
    },

    roundupTarget() {
      const cardFeeFactor =
        1 + parseFloat(this.$lodash.get(this.invoice, "job.brand.card_fee", 0)) / 100;

      const invoiceAmount = this.invoice.lines_total * cardFeeFactor;

      // This amount already include the cardFee
      return Math.ceil(invoiceAmount / 5) * 5;
    },

    roundupAmount() {
      const cardFeeFactor =
        1 + parseFloat(this.$lodash.get(this.invoice, "job.brand.card_fee", 0)) / 100;

      const invoiceAmount = this.invoice.lines_total * cardFeeFactor;
      const targetAmount = Math.ceil(invoiceAmount / 5) * 5;

      // This amount already include the cardFee
      return targetAmount - invoiceAmount;
    },

    dueAmount() {
      const cardFeeFactor =
        1 + parseFloat(this.$lodash.get(this.invoice, "job.brand.card_fee", 0)) / 100;

      return (
        (parseFloat(this.invoice.lines_total) + parseFloat(this.form.tribute_amount)) *
        cardFeeFactor
      );
    },
  },

  async created() {
    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      if (modalId === "payModal") {
        this.reset();
      }
    });

    this.fromMobile = this.$lodash.has(this.$route.query, "appointment_id");

    const { uuid } = this.$router.currentRoute.params;

    try {
      const response = await this.$http.get(`/invoices/${uuid}/by-uuid`);
      if (response) {
        this.invoice = response.body;
        this.embedUrl = `${process.env.VUE_APP_API}/invoices/${this.invoice.uuid}/preview-by-uuid`;

        if (this.$lodash.has(this.invoice, "active_tribute")) {
          this.tribute = this.$lodash.get(this.invoice, "active_tribute");
        }

        if (this.fromMobile) {
          pageview("/view-invoice-mobile-app");
        } else {
          pageview("/view-invoice");
        }

        // Browser Title
        if (this.$lodash.get(this, "invoice.invoice_number")) {
          // noinspection JSUnresolvedFunction
          this.$root.setTitle(`Pay Invoice - ${this.$lodash.get(this, "invoice.invoice_number")}`);
        }
      }
    } catch (e) {
      this.invalid = true;
    }
  },

  methods: {
    selectTribute(amount) {
      // Toggling off
      if (this.selectedTribute === amount) {
        this.selectedTribute = null;
        this.form.tribute_amount = 0;
        return;
      }

      this.selectedTribute = amount;

      switch (amount) {
        case "roundup":
          this.form.tribute_amount = this.roundupAmount / this.cardFeeFactor;
          break;

        case "other":
          this.form.tribute_amount = 0;
          break;

        default:
          this.form.tribute_amount = amount;
      }
    },

    onEnd() {
      this.signed = true;
    },

    adjustCCTime(event) {
      if (event.which === 8 && this.cardTime.length === 3) {
        this.cardTime = this.cardTime.slice(0, -1);
      }
    },

    parseTime() {
      if (this.cardTime.length === 2) {
        this.cardTime = `${this.cardTime}/`;
      }

      const time = this.cardTime.split("/");

      this.form.exp_month = time[0].trim();

      if (time.length > 1) {
        this.form.exp_year = `20${time[1].trim()}`;
      } else {
        this.form.exp_year = "";
      }
    },

    reset() {
      this.form.reset();

      this.cardTime = "";
      this.signed = false;
      this.selectedTribute = null;
      this.selectedPaymentMethod = "card";
    },

    onSubmit() {
      // Validations
      if (this.form.number.length !== 15 && this.form.number.length !== 16) {
        this.message = "Invalid Credit Card Number.";
        return false;
      }
      if (this.invoice.job.brand.disable_amex) {
        if (this.form.number.length === 15) {
          this.message = "We do not accept AMEX cards. Please use VISA or MasterCard.";
          return false;
        }
      }
      if (this.form.exp_month.length !== 2 || this.form.exp_year.length !== 4) {
        this.message =
          "Credit Card expiry date is incorrect, please input expiry date in MM/YY format.";
        return false;
      }
      if (this.form.number.length === 15 && this.form.cvc.length !== 4) {
        this.message = "Invalid CVC, please check your input.";
        return false;
      }
      if (this.form.number.length === 16 && this.form.cvc.length !== 3) {
        this.message = "Invalid CVC, please check your input.";
        return false;
      }
      if (this.disabled) {
        this.message = "Cannot pay for this invoice with Credit Card.";
        return false;
      }

      // Get signature
      const { data } = this.$refs.signaturePad.saveSignature();
      const { uuid } = this.$router.currentRoute.params;

      this.form.appointment_id = this.$lodash.get(this.$route.query, "appointment_id");
      this.form.tribute = this.tribute;

      this.form.startProcessing();
      this.form.signature = data;
      this.form
        .post(`/invoices/${uuid}/pay-by-uuid`)
        .then((response) => {
          if (response.status === "successful") {
            if (this.fromMobile) {
              pageview("/invoice-paid-mobile-app");
            } else {
              pageview("/invoice-paid");
            }

            window.toastr.success(response.message, "Success");
            this.$bvModal.hide("payModal");
            this.completed = true;
          } else {
            this.message = response.message;

            this.form.reset();
            this.form.finishProcessing();
          }
        })
        .catch((response) => {
          this.message = response.message;

          this.form.reset();
          this.form.finishProcessing();
        });

      return true;
    },
  },
};
</script>
